import { createSignal, Show } from "solid-js";
import { ArtworksTab } from "./ArtworksTab";
import { ArtistImagesTab } from "./ArtistImagesTab";
import { DepictedPlacesTab } from "./DepictedPlacesTab";
import { CMS } from "./cms";
import { LoadHtml } from "./LoadHtml";
import { GoogleMapsProvider } from "./GoogleMapsProvider";
import { AlbumsTab } from "./AlbumsTab";
import { TimelineTab } from "./tabs/timeline";
import { RelationshipsTab } from "./RelationshipsTab";
import { PreloadArtworksTab } from "./PreloadArtworksTab";
import { cleanImagePath } from "./API";
import { SimilarArtistsTab } from "./tabs/similar-artists";

type TabName = "timeline"|"biography"|"artworks"|"museums"|"relationships"|"awards"|"videos"|"articles"|"library"|"images-of-the-artist"
|"signatures"|"products"|"albums"|"depicted-places"|"similar-artists";

type Props = {
    baseUrl: string;
    settings: any;
    artist: {
        Id: number;
        Name: string;
        StartYear: string;
        EndYear: string;
        Biography: string;
        BiographicalImageUrl: string;
        Awards: string;
        HasMapLocations?: boolean;
        HasRelationships?: boolean;
        HasVideos?: boolean;
        HasArticles?: boolean;
        HasLibraryItems?: boolean;
        HasPortraits?: boolean;
        HasSignatures?: boolean;
        HasProducts?: boolean;
        HasAlbums?: boolean;
        HasDepictedPlaces?: boolean;
        TotalArtworks: number;
    };
    isLoggedIn: boolean; 
    googleMapsApiKey: string;
}

function App(props: Props) {
    const artistId: number = props.artist.Id;
    const artistName: string = props.artist.Name;
    const [tab, setTab] = createSignal<TabName>("artworks");
    return (
        <>
            <GoogleMapsProvider apiKey="AIzaSyAweLj0DgOj1Em6hgX0-yPuOmwlvXLCJ2c">
                <section id="main-template" class="tabs air-gap">
                    <ul class="list-inline tablinkgroup unselectable">
                        <Show when={props.artist.TotalArtworks}>
                            <li onClick={() => setTab("artworks")} classList={{ ["active"]: tab() == "artworks"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Artworks</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={!!props.artist.Biography}>
                            <li onClick={() => setTab("biography")} classList={{ ["active"]: tab() == "biography"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Biography</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={!!props.artist.Awards}>
                            <li onClick={() => setTab("awards")} classList={{ ["active"]: tab() == "awards"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Awards</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasRelationships}>
                            <li onClick={() => setTab("relationships")} classList={{ ["active"]: tab() == "relationships"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Relationships</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={true}>
                            <li onClick={() => setTab("timeline")} classList={{ ["active"]: tab() == "timeline"}}>
                                <span class="tablink">
                                    <CMS id="tab::events">Timeline</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasVideos}>
                            <li onClick={() => setTab("videos")} classList={{ ["active"]: tab() == "videos"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Videos</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasArticles}>
                            <li onClick={() => setTab("articles") } classList={{ ["active"]: tab() == "articles"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Suggested Readings</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasLibraryItems}>
                            <li onClick={() => setTab("library") } classList={{ ["active"]: tab() == "library"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Library</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasPortraits}>
                            <li onClick={() => setTab("images-of-the-artist")} classList={{ ["active"]: tab() == "images-of-the-artist"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Images of the Artist</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasSignatures}>
                            <li onClick={() => setTab("signatures") } classList={{ ["active"]: tab() == "signatures"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Signatures</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasProducts}>
                            <li onClick={() => setTab("products") } classList={{ ["active"]: tab() == "products"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Products</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasAlbums}>
                            <li onClick={() => setTab("albums")} classList={{ ["active"]: tab() == "albums"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Albums</CMS>
                                </span>
                            </li>
                        </Show>
                        <Show when={props.artist.HasDepictedPlaces}>
                            <li onClick={() => setTab("depicted-places")} classList={{ ["active"]: tab() == "depicted-places"}}>
                                <span class="tablink">
                                    <CMS id="tab::artists">Depicted Places</CMS>
                                </span>
                            </li>
                        </Show>
                        <li onClick={() => setTab("similar-artists")} classList={{ ["active"]: tab() == "similar-artists"}}>
                            <span class="tablink">
                                Similar Artists
                            </span>
                        </li>
                    </ul>
                    <div class="tabgroup">
                        <Show when={tab() == "artworks"}>
                            <div class="tab-pane active">
                                <Show when={props.settings.preloadArtworksTab}>
                                    <PreloadArtworksTab
                                        baseUrl={props.baseUrl}
                                        hasMapLocations={props.artist.HasMapLocations}
                                        artistId={artistId}
                                        isLoggedIn={props.isLoggedIn}
                                    />
                                </Show>
                                <Show when={!props.settings.preloadArtworksTab}>
                                    <ArtworksTab
                                        baseUrl={props.baseUrl}
                                        hasMapLocations={props.artist.HasMapLocations}
                                        artistId={artistId}
                                        isLoggedIn={props.isLoggedIn}
                                    />
                                </Show>
                            </div>
                        </Show>
                        <Show when={tab() == "biography"}>
                            <div class="tab-pane active">
                                <div class="container" style="white-space: normal; font-size: 1.25em; padding: 60px 0; font-family: 'Raleway-Regular';">
                                    <div class="row">
                                        <div class="col-md-8">
                                            <div innerHTML={props.artist.Biography}></div>
                                        </div>
                                        <div class="col-md-2">
                                            <Show when={props.artist.BiographicalImageUrl}>
                                                <img
                                                    src={cleanImagePath(props.artist.BiographicalImageUrl) + "?mode=crop&width=300&height=300&anchor=topLeft"}
                                                    alt={artistName}
                                                    class="img-circle"
                                                />
                                            </Show>
                                        </div>
                                    </div>
                                </div>
                            </div>                          
                        </Show>                    
                        <Show when={tab() == "relationships"}>
                            <div class="tab-pane active">
                                <RelationshipsTab artist={{ id: artistId, name: artistName }} />
                            </div>
                        </Show>
                        <Show when={tab() == "timeline"}>
                            <div class="tab-pane active">
                                <TimelineTab artist={props.artist} />
                            </div>                     
                        </Show>
                        <Show when={tab() == "awards"}>
                            <div class="tab-pane active">
                                <div class="container" style="white-space: normal; font-size: 1.25em; padding: 60px 0; font-family: 'Raleway-Regular';">
                                    <div innerHTML={props.artist.Awards}></div>
                                </div>
                            </div>                          
                        </Show>
                        <Show when={tab() == "videos"}>
                            <div class="tab-pane active">
                                <LoadHtml url={"/Artist/Videos/" + artistId}></LoadHtml>
                            </div>                          
                        </Show>
                        <Show when={tab() == "articles"}>
                            <div class="tab-pane active">
                                <LoadHtml url={"/Artist/Articles/" + artistId}></LoadHtml>
                            </div>                          
                        </Show>
                        <Show when={tab() == "library"}>
                            <div class="tab-pane active">
                                <LoadHtml url={"/Artist/Library/" + artistId}></LoadHtml>
                            </div>                          
                        </Show>
                        <Show when={tab() == "images-of-the-artist"}>
                            <div class="tab-pane active">
                                <ArtistImagesTab baseUrl={props.baseUrl} artistId={artistId}></ArtistImagesTab>
                            </div>                          
                        </Show>
                        <Show when={tab() == "signatures"}>
                            <div class="tab-pane active">
                            <LoadHtml url={"/Artist/Signatures/" + artistId}></LoadHtml>
                            </div>                          
                        </Show>
                        <Show when={tab() == "products"}>
                            <div class="tab-pane active">
                                <div class="container">
                                    <LoadHtml url={"/Artist/Products/" + artistId}></LoadHtml>
                                </div>
                            </div>                          
                        </Show>
                        <Show when={tab() == "albums"}>
                            <div class="tab-pane active">
                                <AlbumsTab artistId={artistId}></AlbumsTab>
                            </div>                          
                        </Show>
                        <Show when={tab() == "depicted-places"}>
                            <div class="tab-pane active">
                                <DepictedPlacesTab
                                    onBackToArtworksClicked={setTab("artworks")}
                                    artistName={artistName}
                                    artistId={artistId}></DepictedPlacesTab>
                            </div>                          
                        </Show>
                        <Show when={tab() == "similar-artists"}>
                            <div class="tab-pane active">
                                <SimilarArtistsTab
                                    artistId={artistId}
                                    isLoggedIn={props.isLoggedIn}
                                />
                            </div>
                        </Show>
                    </div>
                </section>
            </GoogleMapsProvider>
        </>
    );
}
export default App;